import { useState, useEffect, useCallback } from 'react';
import { PrayerRequest, PrayerList } from '../types';

// FIX: Removed local gapi declaration. It is now globally defined in types.ts.

const DISCOVERY_DOC = 'https://www.googleapis.com/discovery/v1/apis/drive/v3/rest';
const SCOPES = 'https://www.googleapis.com/auth/drive.appdata';
const FILE_NAME = 'prayer-companion-data.json';
const FOLDER_NAME = 'appDataFolder';

interface DriveSyncData {
    prayers: PrayerRequest[];
    lists: PrayerList[];
    lastUpdated: string;
}

export const useGoogleDriveSync = () => {
    const [isApiLoaded, setIsApiLoaded] = useState(false);
    const [isSignedIn, setIsSignedIn] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const [lastSyncDate, setLastSyncDate] = useState<Date | null>(null);
    const [userProfile, setUserProfile] = useState<gapi.auth2.BasicProfile | null>(null);

    const updateSigninStatus = useCallback((signedIn: boolean) => {
        setIsSignedIn(signedIn);
        if (signedIn) {
            setUserProfile(gapi.auth2.getAuthInstance().currentUser.get().getBasicProfile());
        } else {
            setUserProfile(null);
            setLastSyncDate(null);
        }
    }, []);

    useEffect(() => {
        const script = document.createElement('script');
        script.src = 'https://apis.google.com/js/api.js';
        script.async = true;
        script.defer = true;
        script.onload = () => {
            gapi.load('client:auth2', async () => {
                await gapi.client.init({
                    apiKey: process.env.API_KEY,
                    clientId: process.env.GOOGLE_CLIENT_ID,
                    discoveryDocs: [DISCOVERY_DOC],
                    scope: SCOPES,
                });
                setIsApiLoaded(true);
                const authInstance = gapi.auth2.getAuthInstance();
                updateSigninStatus(authInstance.isSignedIn.get());
                authInstance.isSignedIn.listen(updateSigninStatus);
            });
        };
        document.body.appendChild(script);

        return () => {
            document.body.removeChild(script);
        };
    }, [updateSigninStatus]);

    const signIn = () => gapi.auth2.getAuthInstance().signIn();
    const signOut = () => gapi.auth2.getAuthInstance().signOut();

    const getFileId = useCallback(async (): Promise<string | null> => {
        const response = await gapi.client.drive.files.list({
            spaces: FOLDER_NAME,
            fields: 'files(id, name)',
            q: `name='${FILE_NAME}' and trashed = false`,
        });
        const files = response.result.files;
        return files && files.length > 0 ? files[0].id : null;
    }, []);

    const syncData = useCallback(async (data: { prayers: PrayerRequest[], lists: PrayerList[] }) => {
        if (!isSignedIn || !isApiLoaded) return;
        setIsSyncing(true);
        try {
            let fileId = await getFileId();
            
            const content: DriveSyncData = { ...data, lastUpdated: new Date().toISOString() };
            const blob = new Blob([JSON.stringify(content)], { type: 'application/json' });
            
            const metadata = {
                name: FILE_NAME,
                mimeType: 'application/json',
                ...( !fileId && { parents: [FOLDER_NAME] } ),
            };

            const formData = new FormData();
            formData.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
            formData.append('file', blob);

            const uploadUrl = new URL(`https://www.googleapis.com/upload/drive/v3/files${fileId ? `/${fileId}` : ''}`);
            uploadUrl.searchParams.set('uploadType', 'multipart');
            
            const response = await fetch(uploadUrl.toString(), {
                method: fileId ? 'PATCH' : 'POST',
                headers: {
                    Authorization: `Bearer ${gapi.auth2.getAuthInstance().currentUser.get().getAuthResponse().access_token}`,
                },
                body: formData,
            });

            if (!response.ok) {
                const errorBody = await response.json();
                throw new Error(`Failed to upload file: ${errorBody.error.message}`);
            }

            setLastSyncDate(new Date());

        } catch (error) {
            console.error("Sync error:", error);
        } finally {
            setIsSyncing(false);
        }
    }, [isSignedIn, isApiLoaded, getFileId]);
    
    const restoreData = useCallback(async (): Promise<{ prayers: PrayerRequest[], lists: PrayerList[] } | null> => {
        if (!isSignedIn || !isApiLoaded) return null;
        setIsSyncing(true);
        try {
            const fileId = await getFileId();
            if (!fileId) {
                console.log("No backup file found to restore.");
                alert("No backup file found on your Google Drive.");
                return null;
            }

            const response = await gapi.client.drive.files.get({ fileId, alt: 'media' });
            const data: DriveSyncData = JSON.parse(response.body);
            setLastSyncDate(data.lastUpdated ? new Date(data.lastUpdated) : new Date());
            return { prayers: data.prayers || [], lists: data.lists || [] };
        } catch (error) {
            console.error("Restore error:", error);
            alert("An error occurred while restoring from backup.");
            return null;
        } finally {
            setIsSyncing(false);
        }
    }, [isSignedIn, isApiLoaded, getFileId]);

    return {
        isApiLoaded,
        isSignedIn,
        isSyncing,
        lastSyncDate,
        userProfile,
        signIn,
        signOut,
        syncData,
        restoreData,
    };
};
